import { createApi } from '@reduxjs/toolkit/query/react';
import { idType, selectItemType } from '@src/types';
import { baseQuerySample } from '@store/helpers';
import { DEFAULT_PAGE_SIZE } from '@src/utils/constants';

type getContractorRatesByIdResType = {
  id: number;
  effectiveDate: string;
  expirationDate: string;
  contractDocumentId: number;
  hcpcOrBillingCode: string;
  bucketStartUnits: number;
  bucketEndUnits: number;
  bucketFreeUnits: number;
  baseRate: number;
  unitRate: number;
  markUpValue: number;
  modeOfTransport: selectItemType | null;
  equipmentAndAddon: selectItemType | null;
  lob: selectItemType | null;
  markUpType: selectItemType | null;
  billQualifierType: selectItemType | null;
};

type SearchContractorsArgs = {
  params?: {
    page?: number;
    size?: number;
    sortBy?: string;
    sortDir?: string;
  };
  body?: {
    contractorName?: string;
    tin?: string;
    losId?: number;
    stateId?: number;
    dispatchPlatformId?: number;
    contractorTypeId?: number;
  };
};

type SearchContractorsByTinResponse = {
  status: number;
  message: string;
  contractor: Record<string, any> | null;
};

export type ContractorPortalInfoResponse = {
  id: number;
  name: string;
  tin: string;
  npi: string;
  physicalAddress: Record<string, any> | null;
  mailingAddress: Record<string, any> | null;
  primaryPhone: string;
  primaryPhoneExt: string;
  secondaryPhoneExt: string;
  website: string;
  secondaryPhone: string;
  billingPhone: string;
  secondaryType: string;
  primaryType: string;
  dispatchPhone: string;
  logos: string | null;
  star: number;
  email: string;
  faxNumber: string;
  shortName: string;
  dispatchPlatform: selectItemType | null;
  contractorType: selectItemType | null;
  status: selectItemType | null;
  payors: selectItemType[];
};

export type ContractorPortalInvitationItem = {
  id: number;
  payer?: string;
  payerName?: string;
  payorName?: string;
  name?: string;
  state?: string;
  stateName?: string;
  date?: string;
  acceptedDate?: string;
  invitationDate?: string;
  createdDate?: string;
  status?: string | selectItemType | null;
  payor?: selectItemType | null;
};

export type ContractorPortalInvitationsResponse = {
  content?: ContractorPortalInvitationItem[];
  page?: number;
  size?: number;
  totalElements?: number;
  totalPages?: number;
  linkedPayers?: ContractorPortalInvitationItem[];
  pendingInvitations?: ContractorPortalInvitationItem[];
  linked?: ContractorPortalInvitationItem[];
  pending?: ContractorPortalInvitationItem[];
};

export type ContractorPortalLevelOfServiceCount = {
  losId: number;
  levelOfServiceName: string;
  segmentCount: number;
};

export type ContractorPortalDashboardCard = {
  dateOfService: string;
  totalRides: number;
  ridesByLevelOfService: ContractorPortalLevelOfServiceCount[];
  locked: boolean;
  accepting: boolean;
};

export type ContractorNotificationContact = {
  id: number;
  contactName: string;
  notificationMethod: selectItemType | null;
  emailAddress: string | null;
  mobilePhoneNumber: string | null;
};

export type ContractorNotificationContactsResponse = {
  content: ContractorNotificationContact[];
  page?: number;
  size?: number;
  totalElements?: number;
  totalPages?: number;
};

export const contractorApi = createApi({
  reducerPath: 'Contractors',
  tagTypes: ['Contractors'],
  baseQuery: baseQuerySample,
  endpoints: (builder) => ({
    getContractor: builder.query({
      query: () => ({
        url: '/v2/contractors/contractor',
      }),
    }),
    getContractorById: builder.query<ContractorPortalInfoResponse, void>({
      query: () => ({
        url: '/v2/contractors/portal/contractor',
      }),
    }),
    getContractorPortalDashboard: builder.query<
      ContractorPortalDashboardCard[],
      { contractorId: number }
    >({
      query: ({ contractorId }) => ({
        url: `/v2/search/contractor/portal/dashboard/${contractorId}`,
        method: 'POST',
      }),
      providesTags: ['Contractors'],
    }),
    getContractorPortalInvitations: builder.mutation<
      ContractorPortalInvitationsResponse | ContractorPortalInvitationItem[],
      {
        params?: {
          page?: number;
          size?: number;
          sortBy?: string;
          sortDir?: string;
        };
      } | void
    >({
      query: (args) => ({
        url: '/v2/contractors/portal/contractor/invitation',
        method: 'PUT',
        params: {
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...args?.params,
        },
      }),
    }),
    getContractorPortalInvitationHistory: builder.mutation<
      ContractorPortalInvitationsResponse | ContractorPortalInvitationItem[],
      {
        params?: {
          page?: number;
          size?: number;
          sortBy?: string;
          sortDir?: string;
        };
      } | void
    >({
      query: (args) => ({
        url: '/v2/contractors/portal/contractor/invitation/history',
        method: 'PUT',
        params: {
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...args?.params,
        },
      }),
    }),
    acceptContractorPortalInvitation: builder.mutation<
      unknown,
      { id: number | string }
    >({
      query: ({ id }) => ({
        url: `/v2/contractors/portal/contractor/invitation/${id}/accept`,
        method: 'PUT',
      }),
    }),
    declineContractorPortalInvitation: builder.mutation<
      unknown,
      { id: number | string }
    >({
      query: ({ id }) => ({
        url: `/v2/contractors/portal/contractor/invitation/${id}/decline`,
        method: 'PUT',
      }),
    }),
    searchContractors: builder.mutation({
      query: ({ argument: { body = {} }, params = {} }) => ({
        url: '/v2/search/contractor/search-contractor',
        method: 'POST',
        body,
        params: {
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),
    searchContractorsByTin: builder.query<
      SearchContractorsByTinResponse,
      { tin: string }
    >({
      query: ({ tin }) => ({
        url: `/v2/contractors/info/search-contractors/${tin}`,
      }),
    }),
    createOrUpdateContractorInfo: builder.mutation({
      query: ({ body, isContractorSaved }) => ({
        url: `/v2/contractors/contractor/${isContractorSaved ? 'update' : 'create'}`,
        method: isContractorSaved ? 'PUT' : 'POST',
        body: { ...body, ...(isContractorSaved ? {} : { status: 1 }) },
      }),
    }),
    updateContractorPortalInfo: builder.mutation<
      ContractorPortalInfoResponse,
      { body: Record<string, unknown> }
    >({
      query: ({ body }) => ({
        url: '/v2/contractors/portal/contractor/update',
        method: 'PUT',
        body,
      }),
    }),
    getContractorRates: builder.mutation({
      query: ({ body = {}, params = {} }) => ({
        url: `/v1/contractors/rate`,
        method: 'POST',
        body,
        params: {
          sortBy: 'expirationDate',
          sortDir: 'desc',
          page: 0,
          size: DEFAULT_PAGE_SIZE,
          ...params,
        },
      }),
    }),
    getContractorRatesById: builder.query<
      getContractorRatesByIdResType,
      { id?: idType }
    >({
      query: ({ id }) => ({
        url: `/v1/contractors/rate/${id}`,
      }),
    }),
    createOrUpdateContractorRates: builder.mutation({
      query: ({ id, body, method }) => ({
        url: `/v1/contractors/rate/${id ? `update/${id}` : 'create'}`,
        method,
        body,
      }),
    }),
    getContractorRideAllTags: builder.query<getContractorRatesByIdResType, {}>({
      query: () => ({
        url: `/v1/contractors/tags/tags/all`,
      }),
    }),
    getContractorRideSelectedTags: builder.query<
      getContractorRatesByIdResType,
      {}
    >({
      query: () => ({
        url: `/v1/contractors/tags/tags/contractor`,
      }),
    }),
    updateContractorRideSelectedTags: builder.mutation({
      query: ({ body }) => ({
        url: `/v1/contractors/tags/tags/update`,
        method: 'POST',
        body,
      }),
    }),
    getContractorAddonAllTags: builder.query<getContractorRatesByIdResType, {}>(
      {
        query: () => ({
          url: `/v1/contractors/tags/addons-equipment/all`,
        }),
      }
    ),
    updateContractorAddonAllTags: builder.mutation({
      query: ({ body }) => ({
        url: `/v1/contractors/tags/addons-equipment/update`,
        method: 'POST',
        body,
      }),
    }),
    getContractorAddonSelectedTags: builder.query<
      getContractorRatesByIdResType,
      {}
    >({
      query: () => ({
        url: `/v1/contractors/tags/addons-equipment/contractor`,
      }),
    }),
    contractorBasicAnalytics: builder.query({
      query: ({ query }) => ({
        url: `/v2/search/contractor/basic-analytics?time-frame=${query}`,
      }),
    }),
    checkNpi: builder.query({
      query: ({ npi }) => ({
        url: `/v2/contractors/info/search-contractors/by-npi/${npi}`,
      }),
    }),
    updateContractorNotificationContacts: builder.mutation<
      ContractorNotificationContact,
      { id: idType; body: Partial<ContractorNotificationContact> }
    >({
      query: ({ body, id }) => ({
        url: `/v1/contractors/notification-contacts/update/${id}`,
        method: 'PUT',
        body,
      }),
    }),
    createContractorNotificationContacts: builder.mutation<
      ContractorNotificationContact,
      { body: Partial<ContractorNotificationContact> }
    >({
      query: ({ body }) => ({
        url: `/v1/contractors/notification-contacts/create`,
        method: 'POST',
        body,
      }),
    }),
    getContractorNotificationContacts: builder.query<
      ContractorNotificationContactsResponse,
      Record<string, unknown> | void
    >({
      query: () => ({
        url: `/v1/contractors/notification-contacts`,
        method: 'GET',
      }),
    }),
    getContractorNotificationContactsById: builder.query<
      ContractorNotificationContact,
      { id: idType }
    >({
      query: ({ id }) => ({
        url: `/v1/contractors/notification-contacts/${id}`,
        method: 'GET',
      }),
    }),
    deleteContractorNotificationContactsById: builder.mutation<
      unknown,
      { id: idType }
    >({
      query: ({ id }) => ({
        url: `/v1/contractors/notification-contacts/${id}`,
        method: 'DELETE',
      }),
    }),
  }),
});

export const {
  useContractorBasicAnalyticsQuery,
  useLazyContractorBasicAnalyticsQuery,
  useLazyGetContractorQuery,
  useLazyGetContractorByIdQuery,
  useGetContractorPortalDashboardQuery,
  useGetContractorPortalInvitationsMutation,
  useGetContractorPortalInvitationHistoryMutation,
  useAcceptContractorPortalInvitationMutation,
  useDeclineContractorPortalInvitationMutation,
  useSearchContractorsMutation,
  useLazySearchContractorsByTinQuery,
  useGetContractorRatesMutation,
  useLazyGetContractorRatesByIdQuery,
  useCreateOrUpdateContractorRatesMutation,
  useLazyGetContractorRideAllTagsQuery,
  useLazyGetContractorRideSelectedTagsQuery,
  useLazyGetContractorAddonAllTagsQuery,
  useUpdateContractorRideSelectedTagsMutation,
  useLazyGetContractorAddonSelectedTagsQuery,
  useUpdateContractorAddonAllTagsMutation,
  useCreateOrUpdateContractorInfoMutation,
  useUpdateContractorPortalInfoMutation,
  useLazyCheckNpiQuery,
  useUpdateContractorNotificationContactsMutation,
  useCreateContractorNotificationContactsMutation,
  useLazyGetContractorNotificationContactsQuery,
  useLazyGetContractorNotificationContactsByIdQuery,
  useDeleteContractorNotificationContactsByIdMutation,
} = contractorApi;
